import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Collapse,
  CircularProgress,
  Divider,
  Avatar,
  Chip,
  Tooltip,
  useTheme,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  InputBase,
  MenuItem,
  Select,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PendingIcon from "@mui/icons-material/Pending";
import BlockIcon from "@mui/icons-material/Block";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";

// All possible statuses from prompt image
const statusList = [
  "All",
  "Pending",
  "Paused",
  "In Progress",
  "In Review",
  "Approved",
  "Rejected",
  "Completed",
  "Blocked",
];

// Date range filter list
const dateRangeFilters = [
  { label: "Today", value: "today" },
  { label: "Last Week", value: "last_week" },
  { label: "Last Month", value: "last_month" },
  { label: "Last Year", value: "last_year" },
];

// Color mappings for each status
const statusColors = {
  Pending: "warning",
  Paused: "secondary",
  "In Progress": "info",
  "In Review": "info",
  Approved: "success",
  Rejected: "error",
  Completed: "success",
  Blocked: "error",
  default: "default",
};

// Icons for each status
const statusIcons = {
  Pending: <PendingIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  Paused: <PauseCircleIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  "In Progress": <InfoOutlinedIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  "In Review": <RateReviewOutlinedIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  Approved: <CheckCircleOutlineIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  Rejected: <CancelOutlinedIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  Completed: <AssignmentTurnedInIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
  Blocked: <BlockIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />,
};

// Optionally, use colored Chips for priorities
const priorityColors = {
  High: "error",
  Medium: "warning",
  Low: "success",
  Critical: "error",
  Urgent: "error",
  Normal: "info",
  default: "default",
};

// Helper: Returns "YYYY-MM-DD" for a Date or string
function toISODateString(dateInput) {
  if (typeof dateInput === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(dateInput)) return dateInput.substr(0, 10);
    const d = new Date(dateInput);
    if (!isNaN(d)) return d.toISOString().substr(0, 10);
    return "";
  }
  if (dateInput instanceof Date) {
    const offset = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return offset(dateInput).toISOString().substr(0, 10);
  }
  return "";
}

function addDays(isoDate, numDays) {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + numDays);
  return toISODateString(d);
}

function getDateRange(rangeType) {
  const today = new Date();
  let start;
  let end = toISODateString(today);

  switch (rangeType) {
    case "today":
      start = toISODateString(today);
      end = toISODateString(today);
      break;
    case "last_week":
      {
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - 6);
        start = toISODateString(lastWeekStart);
      }
      break;
    case "last_month":
      {
        const lastMonthStart = new Date(today);
        lastMonthStart.setDate(today.getDate() - 29);
        start = toISODateString(lastMonthStart);
      }
      break;
    case "last_year":
      {
        const lastYearStart = new Date(today);
        lastYearStart.setDate(today.getDate() - 364);
        start = toISODateString(lastYearStart);
      }
      break;
    default:
      start = toISODateString(today);
      break;
  }
  return { start, end };
}

const ProjectOverview = ({ project }) => {
  const counts = React.useMemo(() => {
    const statusCounts = {};
    if (Array.isArray(project.tasks)) {
      project.tasks.forEach((task) => {
        const status = task.status || "Unknown";
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
    }
    return statusCounts;
  }, [project.tasks]);

  const totalTasks = Array.isArray(project.tasks) ? project.tasks.length : 0;
  const completedCount = counts['Completed'] || 0;
  const percentComplete = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 3 },
        py: { xs: 2, sm: 2.2, md: 2.5 },
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2.8,
        bgcolor: "#f3f8fa",
        borderRadius: 3,
        mb: 2.2,
        mt: 1,
        ml: 1,
        mr: 1,
        border: (theme) => `1px solid ${theme.palette.primary.light}22`,
        width: "calc(100% - 16px)",
        boxShadow:
          "0 8px 16px 0 rgba(76, 76, 77, 0.09), 0 4px 18px 0 rgba(66, 133, 244, 0.07), 0 2px 8px 0 rgba(0,0,0,0.03)",
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
          Project Overview
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.7, mt: 0.8, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ fontWeight: 500, pr: 1 }}>
            <b>Total Tasks:</b> {totalTasks}
          </Typography>
          <Box sx={{ minWidth: 160, maxWidth: 260, flex: "0 0 auto", mr: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.3 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#2e7d32", mr: 1 }}>
                Completed:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#12753f" }}>
                {completedCount}/{totalTasks}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentComplete}
              sx={{
                height: 8,
                borderRadius: 2.5,
                background: "#e7eefd",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: percentComplete === 100 ? "#19af77"
                    : percentComplete >= 60 ? "#11b0f5"
                    : percentComplete > 0 ? "#fec82c"
                    : "#dfdfdf",
                  transition: "background-color 0.15s",
                },
                my: 1,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: percentComplete === 100 ? "#21996b"
                  : percentComplete >= 60 ? "#0d7ddc"
                  : percentComplete > 0 ? "#b07b13"
                  : "#a2a8b3",
                fontWeight: 500,
                letterSpacing: 0.1,
                ml: "auto",
                textAlign: "right",
                display: "block"
              }}
            >
              {percentComplete}%
            </Typography>
          </Box>
          {Object.entries(counts).map(([status, count]) => (
            <Chip
              key={status}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                  {statusIcons[status] || null} {status}: {count}
                </Box>
              }
              color={statusColors[status] || "default"}
              size="small"
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                fontWeight: 500,
                fontSize: 13,
                px: 1.2,
                mr: 0.8,
                background: "#fff",
                borderRadius: 1.8,
                borderColor: (theme) => theme.palette.primary.light + "44",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const ClientReports = ({ clientId = 37, organizationId = 3 }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedProjectIds, setExpandedProjectIds] = useState([]);
  const [projectTaskFilters, setProjectTaskFilters] = useState({});
  // For custom range: { [projectId]: { start: YYYY-MM-DD, end: YYYY-MM-DD } }
  const [projectDateFilters, setProjectDateFilters] = useState({});
  const [projectDateRangeFilters, setProjectDateRangeFilters] = useState({});
  const { userData } = useAuthStore();
  const theme = useTheme();

  const getTodayISO = () => {
    const d = new Date();
    return toISODateString(d);
  };

  // Set default for custom date range filter: single date as both start and end
  useEffect(() => {
    if (projects.length > 0) {
      setProjectTaskFilters((prev) => {
        const next = { ...prev };
        projects.forEach((p) => {
          if (!next[p.organization_project_id]) {
            next[p.organization_project_id] = "All";
          }
        });
        return next;
      });
      setProjectDateFilters((prev) => {
        const next = { ...prev };
        projects.forEach((p) => {
          if (!next[p.organization_project_id]) {
            // Default for custom: {start, end}
            next[p.organization_project_id] = {
              start: getTodayISO(),
              end: getTodayISO(),
            };
          } else if (typeof next[p.organization_project_id] === "string") {
            // migrate old style
            next[p.organization_project_id] = {
              start: next[p.organization_project_id],
              end: next[p.organization_project_id],
            };
          }
        });
        return next;
      });
      setProjectDateRangeFilters((prev) => {
        const next = { ...prev };
        projects.forEach((p) => {
          if (!next[p.organization_project_id]) {
            next[p.organization_project_id] = "today";
          }
        });
        return next;
      });
    }
  }, [projects]);

  // Get range for shortcuts
  function getCustomRangeOrDefault(projid) {
    const obj = projectDateFilters[projid];
    if (obj && typeof obj === "object" && obj.start && obj.end) {
      return { ...obj };
    }
    // fallback for migration: use today for both
    const val = typeof obj === "string" ? obj : getTodayISO();
    return { start: val, end: val };
  }

  // Update range (custom only)
  const handleCustomDateChange = (projectId, kind, value) => {
    setProjectDateFilters((prev) => ({
      ...prev,
      [projectId]: {
        ...(prev[projectId] ? prev[projectId] : { start: getTodayISO(), end: getTodayISO() }),
        [kind]: value,
      },
    }));
    setProjectDateRangeFilters((prev) => ({
      ...prev,
      [projectId]: "custom",
    }));
  };

  // Update with date shortcuts (also resets custom range)
  const handleDateRangeChange = (projectId, rangeValue) => {
    setProjectDateRangeFilters((prev) => ({
      ...prev,
      [projectId]: rangeValue,
    }));
    if (rangeValue !== "custom") {
      // reset custom dateFilter to use shortcut range
      setProjectDateFilters((prev) => ({
        ...prev,
        [projectId]: {
          start: getTodayISO(),
          end: getTodayISO(),
        },
      }));
    }
  };

  // The rest is the same as before, except tweaks to filter logic (support custom range) and UI
  const handleProjectToggle = (projectId) => {
    setExpandedProjectIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleTaskStatusFilter = (projectId, status) => {
    setProjectTaskFilters((prev) => ({
      ...prev,
      [projectId]: status,
    }));
  };

  // ANCHOR: Initial Project Fetching
  useEffect(() => {
    let clientIdArr;
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const orgId = userData?.organization?.organization_id || null;
        const token = localStorage.getItem("token");
        const email = userData.activeOrganizationUser.email;

        const resp = await fetch(
          `${MAIN_URL}/api/project-management/clients/by-email/${email}`,
          {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );
        if (resp.ok) {
          const json = await resp.json();
          const clientIds = json.data;
          clientIdArr = Array.isArray(clientIds)
            ? clientIds.map((c) => c.organization_client_id)
            : [];
        }

        const res = await axios.get(
          `${MAIN_URL}/api/project-management/project/clientReports`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              organization_id: orgId,
              client_ids: clientIdArr,
            },
          }
        );
        let data = res.data?.data;
        if (Array.isArray(data)) {
          setProjects(data);
        } else if (Array.isArray(res.data)) {
          setProjects(res.data);
        } else {
          setProjects([]);
        }
      } catch (e) {
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const getPriorityDisplay = (priority) => {
    if (!priority) return "Not Set";
    if (typeof priority === "string") {
      const norm = priority.trim().toLowerCase();
      if (["high", "critical", "urgent"].includes(norm)) return "High";
      if (["medium", "normal", "moderate"].includes(norm)) return "Medium";
      if (["low", "minor"].includes(norm)) return "Low";
      return priority;
    }
    return priority;
  };

  const describeDateRange = (rangeValue) => {
    switch (rangeValue) {
      case "today": return "Today";
      case "last_week": return "Last 7 days";
      case "last_month": return "Last 30 days";
      case "last_year": return "Last 365 days";
      case "custom": return "Custom Range";
      default: return "Today";
    }
  };

  return (
    <Box sx={{ px: 0, maxWidth: "100%", width: "100%", mx: "unset", pb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 2.6,
          mb: 4.5,
          mt: 3,
          ml: { xs: 2, sm: 2, md: 4 },
          mr: { xs: 2, sm: 2, md: 4 },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#111827",
              letterSpacing: 1.2,
              lineHeight: 1.2,
              mb: 0.7,
              textShadow: "0 2px 6px rgba(25,118,210,0.06)",
              px: { xs: 0, md: 0.5 }
            }}
          >
            Client Projects & Task Report
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#4d647d",
              letterSpacing: 0.5,
              fontWeight: 500,
              lineHeight: 1.5,
              mt: 0.3,
            }}
          >
            A comprehensive overview of your client’s projects, tasks, and progress at a glance.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            py: 7,
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <CircularProgress size={46} thickness={4} />
        </Box>
      ) : projects.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 7,
            color: "#748494",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 46, mb: 1, color: "#b0b0c0" }} />
          <Typography variant="h6" sx={{ fontWeight: 400 }}>
            No projects found for this client.
          </Typography>
        </Box>
      ) : (
        projects.map((project) => {
          const rangeValue = projectDateRangeFilters[project.organization_project_id] || "today";
          const customRange = getCustomRangeOrDefault(project.organization_project_id);

          return (
            <Card
              key={project.organization_project_id}
              sx={{
                mb: 3,
                borderRadius: 4,
                boxShadow: "0 16px 48px 0 rgba(100, 116, 139, 0.18), 0 3px 12px 0 rgba(0,0,0,0.10)",
                background: `linear-gradient(100deg, #fafdff 60%, #f1fdff 100%)`,
                border: `1px solid ${theme.palette.primary.light}11`,
                transition: "box-shadow 0.2s",
                width: "100%",
                "&:hover": {
                  boxShadow:
                    "0 32px 60px 0 rgba(66,133,244,0.18), 0 3px 16px 0 rgba(66,133,244,0.14)",
                },
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", pb: 1.5 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.1, pl: 1 }}>
                    {project.project_name}
                    <Typography
                      variant="caption"
                      component="span"
                      color="text.secondary"
                      sx={{ ml: 1.4, fontWeight: 500 }}
                    >
                      (ID: {project.organization_project_id})
                    </Typography>
                  </Typography>

                  <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 0.3, pl: 1 }}>
                    Status:{" "}
                    <Chip
                      size="small"
                      label={project.project_status}
                      color={
                        statusColors[project.project_status] ||
                        statusColors.default
                      }
                      variant="outlined"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 600,
                        fontSize: 14,
                        px: 0.7,
                      }}
                    />
                  </Typography>
                </Box>
                <Tooltip title={expandedProjectIds.includes(project.organization_project_id) ? "Hide Tasks" : "Show Tasks"}>
                  <IconButton
                    onClick={() =>
                      handleProjectToggle(project.organization_project_id)
                    }
                    size="large"
                    sx={{
                      color: theme.palette.primary.main,
                      transition: "transform 0.18s",
                      transform: expandedProjectIds.includes(project.organization_project_id) ? "rotate(180deg)" : "none",
                    }}
                  >
                    {expandedProjectIds.includes(
                      project.organization_project_id
                    ) ? (
                      <ExpandLessIcon sx={{ fontSize: 31 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 31 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </CardContent>
              <Collapse
                in={expandedProjectIds.includes(project.organization_project_id)}
                timeout="auto"
                unmountOnExit
              >
                <Divider sx={{ mb: 1 }} />
                {/* --- PROJECT OVERVIEW --- */}
                <ProjectOverview project={project} />
                {/* --- END PROJECT OVERVIEW --- */}
                {/* --- TASK STATUS FILTER BAR & DATE PICKER AND RANGE --- */}
                <Box
                  sx={{
                    px: { xs: 1, sm: 2 },
                    pt: 1,
                    pb: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 4 },
                    bgcolor: "#eef3fa",
                    border: `1px solid ${theme.palette.primary.light}22`,
                    mb: 2,
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  {/* Status filter as dropdown */}
                  <Box sx={{ minWidth: 180 }}>
                    <Select
                      value={projectTaskFilters[project.organization_project_id] || "All"}
                      onChange={e => handleTaskStatusFilter(project.organization_project_id, e.target.value)}
                      size="small"
                      displayEmpty
                      sx={{
                        background: "#fff",
                        borderRadius: 2,
                        fontWeight: 600,
                        minWidth: 120,
                        mr: 2,
                        "& .MuiSelect-select": {
                          py: "8px",
                          fontSize: "15px",
                          color: "#1a325c",
                          display: "flex",
                          alignItems: "center",
                        }
                      }}
                      renderValue={selected => {
                        const found = statusList.find(s => s === selected);
                        return (
                          <Box sx={{display: "flex", alignItems: "center", gap: 0.8}}>
                            {statusIcons[selected] && selected !== "All" ? statusIcons[selected] : null}
                            {found ? selected : "All"}
                          </Box>
                        );
                      }}
                    >
                      {statusList.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Box sx={{display:"flex", alignItems: "center", gap: 0.8}}>
                            {statusIcons[status] && status !== "All" ? statusIcons[status] : null}
                            {status}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* --- Date Range Filter Dropdown. Date picker visible only when custom selected --- */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      ml: "auto",
                    }}
                  >
                    <Select
                      value={rangeValue}
                      onChange={e => handleDateRangeChange(project.organization_project_id, e.target.value)}
                      size="small"
                      sx={{
                        background: "#fff",
                        borderRadius: 2,
                        fontWeight: 600,
                        minWidth: 135,
                        mr: 1,
                        "& .MuiSelect-select": {
                          py: "6px",
                          fontSize: "15px",
                          color: "#154892",
                        },
                      }}
                    >
                      {dateRangeFilters.map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                    {rangeValue === "custom" && (
                      <>
                        <InputBase
                          type="date"
                          sx={{
                            border: "1px solid #bbd3ee",
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.7,
                            fontWeight: 600,
                            fontSize: "15px",
                            background: "#fff",
                            color: "#2f4259",
                            width: 144,
                            "& input": {
                              p: 0,
                              minWidth: 0,
                              fontWeight: 600,
                              fontSize: "15px",
                              fontFamily: "inherit",
                              letterSpacing: 0.1,
                              color: "#163360",
                              background: "transparent",
                            },
                          }}
                          value={customRange.start}
                          onChange={e =>
                            handleCustomDateChange(
                              project.organization_project_id,
                              "start",
                              e.target.value
                            )
                          }
                        />
                        <Typography sx={{ mx: 1, fontWeight: 500, color: "#333" }}>
                          to
                        </Typography>
                        <InputBase
                          type="date"
                          sx={{
                            border: "1px solid #bbd3ee",
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.7,
                            fontWeight: 600,
                            fontSize: "15px",
                            background: "#fff",
                            color: "#2f4259",
                            width: 144,
                            "& input": {
                              p: 0,
                              minWidth: 0,
                              fontWeight: 600,
                              fontSize: "15px",
                              fontFamily: "inherit",
                              letterSpacing: 0.1,
                              color: "#163360",
                              background: "transparent",
                            },
                          }}
                          value={customRange.end}
                          onChange={e =>
                            handleCustomDateChange(
                              project.organization_project_id,
                              "end",
                              e.target.value
                            )
                          }
                        />
                        <Typography variant="body2" sx={{ ml: 0.8, fontWeight: 600, color: "#335559" }}>
                        
                        </Typography>
                      </>
                    )}
                    {rangeValue !== "custom" && (
                      <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 600, color: "#335559" }}>
                   
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* --- END TASK STATUS FILTER BAR & DATE PICKER --- */}
                <Box
                  sx={{
                    px: { xs: 0.4, sm: 2 },
                    pb: 2.2,
                    bgcolor: "#f6fbfc",
                    borderRadius: 3,
                    border: `1px dashed ${theme.palette.secondary.light}44`,
                    mx: 1,
                    width: "calc(100% - 16px)",
                    maxWidth: "100%",
                  }}
                >
                  {Array.isArray(project.tasks) && project.tasks.length > 0 ? (
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        background: "transparent",
                        boxShadow: "none",
                        borderRadius: 2,
                        border: "none",
                        maxWidth: "100%",
                      }}
                    >
                      <Table size="small" sx={{
                        minWidth: 700,
                        "& thead th": {
                          bgcolor: "#e9f1fa",
                          fontWeight: 700,
                          color: theme.palette.primary.dark,
                          borderBottom: "2px solid #e3e8f1"
                        },
                        "& td": {
                          borderColor: "#e5ebf7",
                          fontSize: 15,
                          verticalAlign: "top"
                        }
                      }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Task Title</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Scheduled Date</TableCell>
                            <TableCell>Priority</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[
                            ...project.tasks
                          ]
                            .map((task) => {
                              let scheduledDate = task.scheduled_date;
                              if (
                                (!scheduledDate || scheduledDate === "") &&
                                Array.isArray(task.schedules) &&
                                task.schedules.length > 0
                              ) {
                                scheduledDate = task.schedules[0]?.scheduled_date;
                              }
                              return { ...task, _scheduledDate: scheduledDate };
                            })
                            .filter((task) => {
                              const filterStatus = projectTaskFilters[project.organization_project_id] || "All";
                              const scheduledISO = task._scheduledDate ? toISODateString(task._scheduledDate) : "";
                              const statusMatch =
                                filterStatus === "All" ||
                                (task.status && task.status === filterStatus);

                              const filterRangeType = projectDateRangeFilters[project.organization_project_id] || "today";

                              let dateMatch;
                              if (filterRangeType === "custom") {
                                const { start, end } = getCustomRangeOrDefault(project.organization_project_id);
                                // scheduledISO inside [start, end] interval
                                dateMatch =
                                  !!scheduledISO &&
                                  scheduledISO >= start &&
                                  scheduledISO <= end;
                              } else if (["today", "last_week", "last_month", "last_year"].includes(filterRangeType)) {
                                const { start, end } = getDateRange(filterRangeType);
                                dateMatch =
                                  !!scheduledISO &&
                                  scheduledISO >= start &&
                                  scheduledISO <= end;
                              } else {
                                dateMatch = true;
                              }
                              return statusMatch && dateMatch;
                            })
                            .sort((a, b) => {
                              const dateA = a._scheduledDate ? new Date(a._scheduledDate) : new Date(0);
                              const dateB = b._scheduledDate ? new Date(b._scheduledDate) : new Date(0);
                              return dateB - dateA;
                            })
                            .map((task) => (
                              <TableRow
                                key={task.organization_project_task_id}
                                sx={{
                                  background: "#fff",
                                  "&:hover": { background: "#f4faff" },
                                  borderLeft: `6px solid ${
                                    statusColors[task.status] === "success"
                                      ? "#19af77"
                                      : statusColors[task.status] === "info"
                                      ? "#1976d2"
                                      : statusColors[task.status] === "warning"
                                      ? "#eab308"
                                      : "#c7c7c7"
                                  }`
                                }}
                              >
                                {/* Task Title */}
                                <TableCell sx={{ fontWeight: 700, color: "#212857" }}>
                                  {task.task_title}
                                </TableCell>
                                {/* Status */}
                                <TableCell>
                                  <Chip
                                    color={statusColors[task.status] || statusColors.default}
                                    variant="filled"
                                    size="small"
                                    label={
                                      <Box sx={{ display: "flex", alignItems: "center", px: 0.3 }}>
                                        {(statusIcons[task.status] || null)} {task.status || "Unknown"}
                                      </Box>
                                    }
                                    sx={{
                                      fontWeight: 500,
                                      textTransform: "capitalize",
                                      letterSpacing: 0.07,
                                    }}
                                  />
                                </TableCell>
                                {/* Scheduled Date */}
                                <TableCell>
                                  {task._scheduledDate
                                    ? new Date(task._scheduledDate).toLocaleDateString()
                                    : "--"}
                                </TableCell>
                                {/* Priority */}
                                <TableCell>
                                  <Chip
                                    label={getPriorityDisplay(task.priority)}
                                    size="small"
                                    color={
                                      priorityColors[getPriorityDisplay(task.priority)] ||
                                      "default"
                                    }
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "12px",
                                      textTransform: "capitalize",
                                      background:
                                        getPriorityDisplay(task.priority) === "High"
                                          ? "#fff3f2"
                                          : getPriorityDisplay(task.priority) === "Medium"
                                          ? "#fffde9"
                                          : getPriorityDisplay(task.priority) === "Low"
                                          ? "#e7fdf6"
                                          : "#f7fafd",
                                      color:
                                        getPriorityDisplay(task.priority) === "High"
                                          ? "#cb1b12"
                                          : getPriorityDisplay(task.priority) === "Medium"
                                          ? "#cd7716"
                                          : getPriorityDisplay(task.priority) === "Low"
                                          ? "#197c4b"
                                          : "#4d5b79",
                                      borderRadius: 1.3,
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      sx={{
                        py: 4,
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        color: "#b0b0b0",
                        fontStyle: "italic",
                      }}
                    >
                      <PendingIcon sx={{ fontSize: 40, mb: 2, color: "#b0bbc0" }} />
                      <Typography variant="body1" sx={{ mt: 0, fontWeight: 500 }}>
                        No tasks found for this project.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Card>
          );
        })
      )}
    </Box>
  );
};

export default ClientReports;